import { useState } from 'react';

import { SlackImportChannelEndpoint } from '@core/types/types.endpoint.slack';
import { timeAgo } from '@core/util/util.timeAgo';
import { Button } from '@frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog';
import { Label } from '@frontend/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@frontend/components/ui/select';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useDirectoryChannels } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

export function SettingsImportModal({
  isReadOnly = false,
}: {
  isReadOnly?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const updater = useDirectoryUpdater();
  const channels = useDirectoryChannels({
    excludeDeleted: true,
    requireQbInChannel: true,
  });

  const [selectedChannelId, setSelectedChannelId] = useState<string>(
    channels[0]?.id ?? '',
  );
  const [selectedDays, setSelectedDays] = useState('30');

  const selectedChannel = channels.find(
    (channel) => channel.id === selectedChannelId,
  );

  const submitter = useSubmitter(async () => {
    const [, res] = await callApi<SlackImportChannelEndpoint>(
      '/slack/import/channel',
      {
        channelId: selectedChannelId,
        days: parseInt(selectedDays, 10),
      },
    );

    if (res) {
      updater(res);
      setIsOpen(false);
    }
  });

  if (!selectedChannel) {
    return null;
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)} size="sm" disabled={isReadOnly}>
        Import History
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import History from Slack</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Channel</Label>
              <Select
                value={selectedChannelId}
                onValueChange={setSelectedChannelId}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((channel) => (
                    <SelectItem key={channel.id} value={channel.id}>
                      # {channel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-semibold">Length</Label>
              <Select value={selectedDays} onValueChange={setSelectedDays}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-sm">
              This will <b>extract Q&As</b> going{' '}
              <b>{selectedDays} days back</b> since the bot joined the channel (
              {timeAgo(selectedChannel.created_at)})
              <br />
              <br />
              They will be added as <b>Unverified</b> in Question Base and we
              recommend verifying them afterwards for the best results.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={submitter.submit}
              disabled={submitter.isSubmitting}
            >
              {submitter.isSubmitting ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
